import { Footer, Header } from "./components";

interface Post {
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  gradient: string;
  accent: string;
}

const posts: Post[] = [
  {
    title: "Raising the Ramparts: Building Mighty Castle",
    excerpt:
      "How a weekend side-project grew into a fortress of ideas. The tools, the missteps, and the mortar that held it all together.",
    category: "Behind the Walls",
    author: "The Castellan",
    date: "Jul 28, 2026",
    readTime: "6 min read",
    gradient: "from-fuchsia-500 via-purple-500 to-indigo-500",
    accent: "bg-fuchsia-100 text-fuchsia-700",
  },
  {
    title: "The Art of the Drawbridge",
    excerpt:
      "Great design invites people in and keeps the trolls out. Lessons on approachable interfaces from medieval engineering.",
    category: "Design",
    author: "Mara Stone",
    date: "Jul 21, 2026",
    readTime: "4 min read",
    gradient: "from-amber-400 via-orange-500 to-rose-500",
    accent: "bg-amber-100 text-amber-700",
  },
  {
    title: "Torchlight & TypeScript",
    excerpt:
      "Lighting the darkest corners of a codebase. A practical guide to types that guard your keep against runtime dragons.",
    category: "Engineering",
    author: "Devin Ward",
    date: "Jul 14, 2026",
    readTime: "8 min read",
    gradient: "from-emerald-400 via-teal-500 to-cyan-500",
    accent: "bg-emerald-100 text-emerald-700",
  },
  {
    title: "A Feast for the Senses",
    excerpt:
      "Colour, motion, and delight. Why a little celebration in your UI keeps visitors coming back to the banquet hall.",
    category: "Culture",
    author: "Isolde Fenn",
    date: "Jul 7, 2026",
    readTime: "5 min read",
    gradient: "from-sky-400 via-blue-500 to-violet-500",
    accent: "bg-sky-100 text-sky-700",
  },
  {
    title: "Guarding the Gate: Web Security Basics",
    excerpt:
      "Every castle needs a watch. Simple, sturdy practices to keep your visitors — and their data — safe from raiders.",
    category: "Security",
    author: "Devin Ward",
    date: "Jun 30, 2026",
    readTime: "7 min read",
    gradient: "from-rose-500 via-pink-500 to-fuchsia-500",
    accent: "bg-rose-100 text-rose-700",
  },
  {
    title: "Mapping the Kingdom",
    excerpt:
      "Information architecture as cartography. How to draw a map your visitors can actually follow to the treasure.",
    category: "Design",
    author: "Mara Stone",
    date: "Jun 23, 2026",
    readTime: "6 min read",
    gradient: "from-lime-400 via-green-500 to-emerald-500",
    accent: "bg-lime-100 text-lime-700",
  },
];

const [featured, ...rest] = posts;

function Hero() {
  return (
    <section class="relative overflow-hidden px-6 py-24 text-center">
      <div class="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-fuchsia-600/30 blur-3xl" />
      <div class="pointer-events-none absolute top-10 right-0 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
      <div class="pointer-events-none absolute bottom-0 left-0 h-72 w-72 rounded-full bg-indigo-600/20 blur-3xl" />
      <div class="relative mx-auto max-w-3xl">
        <span class="inline-block rounded-full border border-white/15 bg-white/5 px-4 py-1 text-sm font-medium text-slate-200">
          ✨ Tales, tools & turrets from mightycastle.net
        </span>
        <h1 class="mt-6 text-5xl font-black tracking-tight text-white sm:text-7xl">
          Stories from the
          <span class="block bg-gradient-to-r from-fuchsia-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
            Mighty Castle
          </span>
        </h1>
        <p class="mx-auto mt-6 max-w-xl text-lg text-slate-300">
          A colourful keep for ideas on design, engineering, and building things
          worth defending. Pull up a chair by the hearth.
        </p>
        <div class="mt-8 flex items-center justify-center gap-4">
          <a
            href="/#posts"
            class="rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 px-6 py-3 font-semibold text-white shadow-lg shadow-cyan-500/25 transition hover:scale-105"
          >
            Read the latest
          </a>
        </div>
      </div>
    </section>
  );
}

function FeaturedPost({ post }: { post: Post }) {
  return (
    <section class="mx-auto max-w-6xl px-6">
      <a
        href="/posts"
        class="group grid overflow-hidden rounded-3xl border border-white/10 bg-slate-900/50 shadow-2xl transition hover:border-white/20 md:grid-cols-2"
      >
        <div class={`relative min-h-56 bg-gradient-to-br ${post.gradient}`}>
          <span class="absolute top-4 left-4 rounded-full bg-black/30 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
            ⭐ Featured
          </span>
        </div>
        <div class="flex flex-col justify-center gap-4 p-8">
          <span class={`w-fit rounded-full px-3 py-1 text-xs font-bold ${post.accent}`}>
            {post.category}
          </span>
          <h2 class="text-3xl font-black text-white transition group-hover:text-fuchsia-300">
            {post.title}
          </h2>
          <p class="text-slate-300">{post.excerpt}</p>
          <div class="mt-2 flex items-center gap-3 text-sm text-slate-400">
            <span class="font-semibold text-slate-200">{post.author}</span>
            <span>·</span>
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>
      </a>
    </section>
  );
}

function PostCard({ post }: { post: Post }) {
  return (
    <a
      href="/posts"
      class="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-900/50 shadow-lg transition hover:-translate-y-1 hover:border-white/20"
    >
      <div class={`h-40 bg-gradient-to-br ${post.gradient}`} />
      <div class="flex flex-1 flex-col gap-3 p-6">
        <span class={`w-fit rounded-full px-3 py-1 text-xs font-bold ${post.accent}`}>
          {post.category}
        </span>
        <h3 class="text-xl font-bold text-white transition group-hover:text-cyan-300">
          {post.title}
        </h3>
        <p class="flex-1 text-sm text-slate-400">{post.excerpt}</p>
        <div class="mt-2 flex items-center gap-2 text-xs text-slate-500">
          <span class="font-semibold text-slate-300">{post.author}</span>
          <span>·</span>
          <span>{post.date}</span>
          <span>·</span>
          <span>{post.readTime}</span>
        </div>
      </div>
    </a>
  );
}

function App() {
  return (
    <div class="min-h-screen bg-slate-950 text-slate-100 selection:bg-fuchsia-500/40">
      <Header />
      <main>
        <Hero />
        <FeaturedPost post={featured} />
        <section id="posts" class="mx-auto max-w-6xl px-6 pt-20">
          <div class="mb-8 flex items-end justify-between">
            <h2 class="text-3xl font-black text-white">Latest posts</h2>
            <a href="/posts" class="text-sm font-semibold text-fuchsia-400 hover:text-fuchsia-300">
              View all →
            </a>
          </div>
          <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((post) => (
              <PostCard post={post} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default App;
